// PlanPanel — the Flight Plan dropdown that overlays the map.
//
// Sections (top to bottom, per spec 2026-05-08):
//   1. Route entry  (sequential ident OR tap-map-to-add)
//   2. Aircraft       (existing aircraft profiles)
//   3. Cruise altitude (feet MSL)
//   4. Procedures (stub — coming-soon section header)
//   5. ETD time wheel (date + hour + minute, 24h local)
//   6. Route advisor (stub — recently filed IFR plans)
//   7. Live calc strip: DIST · ETE · ETA · FUEL · Avg wind
//   8. Pack button (stub — opens Pack sheet, ties to docs/pack-for-flight-spec.md)
//
// The performance-profile picker lives inside Section 2 (Aircraft)
// since it depends on the selected aircraft's published profiles.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../shell/Icon';
import { AlertTriangle, GripVertical, Repeat } from '../shell/Icon';
import { HoldAtWaypointModal } from './HoldAtWaypointModal';
import { usePlanStore } from '../stores/planStore';
import type { PlanPoint, PlanPointKind } from '../stores/planStore';
import {
  nextCompliantAltitude,
  isHemisphericCompliant,
  defaultCompliantCruise,
  hemisphericRuleLabel,
  compliantAltitudes,
  formatAltitudeForDisplay,
} from '../lib/hemisphericRule';
import { useAircraftStore } from '../stores/aircraftStore';
import { computeReorderTarget } from '../lib/reorderMath';
import ProceduresSection from './ProceduresSection';
import RouteAdvisor from './RouteAdvisor';
import { RouteProfile } from '../components/RouteProfile';
import { calcFlight, formatHm, addMinutesIso } from '../lib/flightCalc';
import type { WindAloft } from '../lib/flightCalc';
import { sampleWindsAlongRoute } from '../lib/winds';
import { resolveIdent } from '../lib/ident';
import { searchAny, SearchHit } from '../lib/search';
import { formatNm, formatBrg, bearingDeg } from '../lib/geo';
import TimeWheel, { nextFiveMinuteSlotLocal, composeIsoFromParts } from '../map/TimeWheel';
import { createFlight, flightTitle } from '../lib/flights';
import { useIsDesktop } from '../shell/useBreakpoint';
import { usePackStore } from '../lib/pack';
import { useUiStore } from '../stores/uiStore';
import PackProgressSheet from './PackProgressSheet';
import { formatLocalTime } from '../lib/timeFormat';
import { checkRouteVsTfrs, type TfrConflict } from '../lib/routeConflictCheck';
import { FIRE_TFRS_URL } from '../map/FireMissionLayer';
import { findTerrainClearanceRoute, type PassFinderDiagnostics } from '../lib/passFinder';
import { findCanyonRoute, type CanyonRouterDiagnostics } from '../lib/canyonRouter';

interface Props {
  onClose: () => void;
  /** True when "tap-map-to-add" mode is enabled (controlled by parent). */
  planClick: boolean;
  setPlanClick: (v: boolean) => void;
}

export default function PlanPanel({ onClose, planClick, setPlanClick }: Props) {
  const nav = useNavigate();
  const route       = usePlanStore((s) => s.route);
  const etd         = usePlanStore((s) => s.etd);
  const cruiseAlt   = usePlanStore((s) => s.cruiseAlt);
  const cruiseRule  = usePlanStore((s) => s.cruiseRule);
  const aircraftId  = usePlanStore((s) => s.aircraftId);
  const performanceProfileId = usePlanStore((s) => s.performanceProfileId);

  const add              = usePlanStore((s) => s.add);
  const remove           = usePlanStore((s) => s.remove);
  const move             = usePlanStore((s) => s.move);
  const clear            = usePlanStore((s) => s.clear);
  const replaceEnrouteSegment = usePlanStore((s) => s.replaceEnrouteSegment);
  const setEtd           = usePlanStore((s) => s.setEtd);
  const setCruise        = usePlanStore((s) => s.setCruiseAlt);
  const setCruiseRule    = usePlanStore((s) => s.setCruiseRule);
  const setAcId          = usePlanStore((s) => s.setAircraftId);
  const setPerfProfileId = usePlanStore((s) => s.setPerformanceProfileId);

  const aircraftList   = useAircraftStore((s) => s.list);
  const selectedAcId   = useAircraftStore((s) => s.selectedId);

  // Default to the active aircraft on first open.
  useEffect(() => {
    if (aircraftId == null && selectedAcId) setAcId(selectedAcId);
  }, [aircraftId, selectedAcId, setAcId]);

  const aircraft = useMemo(
    () => aircraftList.find((a) => a.id === aircraftId) ?? null,
    [aircraftList, aircraftId],
  );

  // Resolve the active performance profile. Mirrors the resolution
  // logic in FlightDetailPage: explicit pick → default-flagged → first
  // available → null. 2026-05-17.
  const perfProfile = useMemo(() => {
    if (!aircraft) return null;
    if (performanceProfileId) {
      const explicit = aircraft.performanceProfiles.find((p) => p.id === performanceProfileId);
      if (explicit) return explicit;
    }
    return aircraft.performanceProfiles.find((p) => p.isDefault)
        ?? aircraft.performanceProfiles[0]
        ?? null;
  }, [aircraft, performanceProfileId]);

  // ── Hemispheric rule state ──────────────────────────────────────────
  // Warning shown when the altitude was auto-bumped to be compliant.
  const [altWarning, setAltWarning] = useState<string | null>(null);
  // Boss override: when on, the dropdown exposes EVERY 1000-ft
  // increment (including hemispheric-non-compliant ones) and the
  // auto-bump is disabled. Useful for ATC-assigned altitudes,
  // training, or non-CONUS rule sets. 2026-05-17.
  const [altOverride, setAltOverride] = useState(false);

  // Terrain-clearance auto-router (Wiley feature 2026-05-19).
  // Pilot picks corridor width + whether to avoid TFRs; results land
  // in the route as detour waypoints via replaceEnrouteSegment.
  const [autoRouteBusy, setAutoRouteBusy] = useState(false);
  const [autoRouteErr, setAutoRouteErr] = useState<string | null>(null);
  const [autoRouteInfo, setAutoRouteInfo] =
    useState<PassFinderDiagnostics | null>(null);
  const [corridorNm, setCorridorNm] = useState<number>(10);
  const [avoidTfrs, setAvoidTfrs] = useState<boolean>(false);
  // Canyon router (client-side, terrain-tile-driven). Beta toggle.
  // When ON we run a dense A* in a Web Worker against the in-memory
  // Terrarium DEM tiles instead of calling /api/route/pass-finder.
  // Resolves the O46→36S "too coarse to see the river valley" report
  // 2026-05-19 PT.
  const [useCanyonRouter, setUseCanyonRouter] = useState<boolean>(false);
  const [canyonProgress, setCanyonProgress] = useState<string | null>(null);

  // Trip-direction magnetic course used by the hemispheric rule. We
  // base this on the OVERALL trip direction (origin airport →
  // destination airport great-circle bearing), NOT on the first leg.
  // Boss reported 2026-05-17 that with a SID loaded the first leg's
  // climb-out heading (e.g. FOBRO1 northbound to DIMGE) was flipping
  // the picker into the wrong hemisphere even though the cruise
  // portion of the flight ran southbound from O46 to KSTS. Same trick
  // ForeFlight and Garmin Pilot use — hemispheric rule is about CRUISE
  // direction, not climb-out.
  const MAG_VAR_DEG = 15;
  // (Claude audit 2026-05-18 finding 8) Hoisted out of the zustand
  // selector. Previously every plan-store mutation (every keystroke in
  // remarks, every TimeWheel tick, every setCruise from the bumper
  // effect) re-ran this selector — which called `s.legs()` as a
  // fallback, allocating a Leg[] and running O(N) trig per call. Now
  // we read `route` once and useMemo on a content key.
  const tripDirTrueCourse = useMemo<number | null>(() => {
    const r = route as Array<PlanPoint & { kind?: string }>;
    const firstAp = r.find((p) => p.kind === 'airport');
    let lastAp: (PlanPoint & { kind?: string }) | undefined;
    for (let i = r.length - 1; i >= 0; i--) {
      if (r[i].kind === 'airport') { lastAp = r[i]; break; }
    }
    if (firstAp && lastAp && firstAp !== lastAp) {
      return bearingDeg(firstAp.lat, firstAp.lon, lastAp.lat, lastAp.lon);
    }
    if (r.length >= 2) {
      const a = r[0]; const b = r[r.length - 1];
      return bearingDeg(a.lat, a.lon, b.lat, b.lon);
    }
    return null;
  }, [route]);
  const tripMagCourse = tripDirTrueCourse != null
    ? ((tripDirTrueCourse - MAG_VAR_DEG) + 360) % 360
    : 90; // default eastbound

  // Helper: build HemisphericInput from current state.
  const hemisphericInput = { magneticCourseDeg: tripMagCourse, rule: cruiseRule };

  // Derived: altitude dropdown options.
  //   - Default mode: only hemispheric-compliant altitudes.
  //   - Override mode: every 1000-ft increment from 3000 to 17500
  //     (VFR) / 41000 (IFR). 2026-05-17.
  const altOptions = useMemo(
    () => {
      if (altOverride) {
        // Override mode: 500-ft increments (boss request 2026-05-19).
        // Lets pilots pick MEA-style odd-numbered + half-step altitudes
        // common in mountain helo / terrain-following ops.
        const maxFt = cruiseRule === 'vfr' ? 17500 : 41000;
        const out: number[] = [];
        for (let alt = 3000; alt <= maxFt; alt += 500) out.push(alt);
        return out;
      }
      return compliantAltitudes(hemisphericInput);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cruiseRule, tripMagCourse, altOverride],
  );

  // Trigger A: auto-bump when aircraft changes. Skipped in override
  // mode (boss explicitly took manual control of altitude).
  const prevAircraftIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (aircraftId === prevAircraftIdRef.current) return;
    prevAircraftIdRef.current = aircraftId;
    if (altOverride) return;

    const defaultCruiseFt = aircraft?.altitudes?.defaultCruiseFt;
    if (defaultCruiseFt == null) return;

    const input = hemisphericInput;
    const compliant = defaultCompliantCruise(defaultCruiseFt, input);

    if (cruiseAlt == null) {
      // No altitude set yet — silently default to the compliant altitude.
      setCruise(compliant);
      setAltWarning(null);
    } else if (!isHemisphericCompliant(cruiseAlt, input)) {
      const bumped = nextCompliantAltitude(cruiseAlt, input);
      setAltWarning(
        `Adjusted from ${formatAltitudeForDisplay(cruiseAlt)} → ${formatAltitudeForDisplay(bumped)} ` +
        `(${hemisphericRuleLabel(input)}).`,
      );
      setCruise(bumped);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftId]);

  // Trigger B: re-check when the trip course crosses the 180° boundary.
  // Skipped in override mode.
  const prevMagCourseRef = useRef<number>(tripMagCourse);
  useEffect(() => {
    const prev = prevMagCourseRef.current;
    prevMagCourseRef.current = tripMagCourse;
    if (altOverride) return;
    const prevEast = prev >= 0 && prev <= 179;
    const curEast = tripMagCourse >= 0 && tripMagCourse <= 179;
    if (prevEast === curEast) return;
    if (cruiseAlt == null) return;

    const input = hemisphericInput;
    if (!isHemisphericCompliant(cruiseAlt, input)) {
      const bumped = nextCompliantAltitude(cruiseAlt, input);
      setAltWarning(
        `Adjusted from ${formatAltitudeForDisplay(cruiseAlt)} → ${formatAltitudeForDisplay(bumped)} ` +
        `(${hemisphericRuleLabel(input)}).`,
      );
      setCruise(bumped);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripMagCourse]);

  // Trigger C: re-validate when VFR/IFR toggle flips. Skipped in
  // override mode.
  const prevRuleRef = useRef<'vfr' | 'ifr'>(cruiseRule);
  useEffect(() => {
    if (cruiseRule === prevRuleRef.current) return;
    prevRuleRef.current = cruiseRule;
    if (altOverride) return;
    if (cruiseAlt == null) return;

    const input = { magneticCourseDeg: tripMagCourse, rule: cruiseRule };
    if (!isHemisphericCompliant(cruiseAlt, input)) {
      const bumped = nextCompliantAltitude(cruiseAlt, input);
      setAltWarning(
        `Adjusted from ${formatAltitudeForDisplay(cruiseAlt)} → ${formatAltitudeForDisplay(bumped)} ` +
        `(${hemisphericRuleLabel(input)}).`,
      );
      setCruise(bumped);
    } else {
      setAltWarning(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cruiseRule]);

  // Per-leg winds aloft, sampled at the leg midpoints (vector-averaged
  // over 3 sample points for legs >100nm). Refetched whenever the route,
  // cruise altitude, or ETD changes. Defaults to no-wind on first render
  // (and stays no-wind if the upstream is unreachable) so calcFlight
  // always has *some* result to show — the wind correction just lands a
  // beat later than the initial geometry.
  const [winds, setWinds] = useState<(WindAloft | null)[] | null>(null);
  const [windsLoading, setWindsLoading] = useState(false);
  useEffect(() => {
    if (route.length < 2 || cruiseAlt == null) { setWinds(null); return; }
    let cancelled = false;
    setWindsLoading(true);
    sampleWindsAlongRoute(route, cruiseAlt, etd ?? undefined).then((w) => {
      if (cancelled) return;
      setWinds(w);
      setWindsLoading(false);
    }).catch(() => {
      if (!cancelled) setWindsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Re-run on actual content change, not array identity from the store.
    route.map((p) => `${p.ident}:${p.lat.toFixed(3)},${p.lon.toFixed(3)}`).join('|'),
    cruiseAlt,
    etd,
  ]);

  // ---- Route vs TFR conflict check -----------------------------------------
  // Fetch TFRs and check the current route + altitude against them.
  // Refreshed whenever the route or cruise altitude changes.
  // Uses a 60-second in-memory cache so re-opening the panel doesn't
  // refetch on every keystroke.
  const [tfrConflicts, setTfrConflicts] = useState<TfrConflict[]>([]);
  const [conflictsDismissed, setConflictsDismissed] = useState(false);
  // Reset dismissal whenever route or altitude changes so a new route
  // shows the banner again.
  const conflictCheckKey = [
    route.map((p) => `${p.ident}:${p.lat.toFixed(3)},${p.lon.toFixed(3)}`).join('|'),
    cruiseAlt,
  ].join('|');
  const prevConflictKey = useRef<string | null>(null);
  useEffect(() => {
    if (conflictCheckKey !== prevConflictKey.current) {
      setConflictsDismissed(false);
      prevConflictKey.current = conflictCheckKey;
    }
  }, [conflictCheckKey]);

  useEffect(() => {
    if (route.length < 2 || cruiseAlt == null) {
      setTfrConflicts([]);
      return;
    }
    let cancelled = false;
    const routeCoords: [number, number][] = route.map((p) => [p.lon, p.lat]);

    fetch(FIRE_TFRS_URL, { cache: 'default' })
      .then((r) => r.ok ? r.json() : null)
      .then((fc: GeoJSON.FeatureCollection | null) => {
        if (cancelled || !fc) return;
        const conflicts = checkRouteVsTfrs({
          routeCoords,
          cruiseAltFt: cruiseAlt,
          tfrFeatures: fc.features ?? [],
        });
        setTfrConflicts(conflicts);
      })
      .catch(() => {
        // Network errors: silently skip the check so we don't spook
        // the pilot with a fetch-failure state.
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictCheckKey]);

  const calc = useMemo(
    () => calcFlight({ route, aircraft, cruiseAlt, performanceProfileId, magVarDeg: 15, winds: winds ?? undefined }),
    [route, aircraft, cruiseAlt, performanceProfileId, winds],
  );

  // Stable identity for the route content (vs array identity from
  // the store). Used by the Dispatch & comms section to memoize the
  // polygon spatial-join, which is otherwise O(samples × polygons)
  // and we'd rather not redo it on every keystroke.
  const routeKey = route
    .map((p) => `${p.ident}:${p.lat.toFixed(3)},${p.lon.toFixed(3)}`)
    .join('|');
  const eta = etd && calc.totalEteMinutes != null
    ? addMinutesIso(etd, calc.totalEteMinutes)
    : null;
  // Headwind/tailwind summary across the route — negative = headwind
  // (slowing us), positive = tailwind. NaN-safe.
  const avgHeadwindKt = useMemo(() => {
    if (!winds || winds.length === 0 || calc.legs.length === 0) return null;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < calc.legs.length; i++) {
      const w = winds[i];
      const leg = calc.legs[i];
      if (!w || !leg.tasKt || leg.groundspeedKt == null) continue;
      // Tailwind is GS - TAS along the course; positive = tailwind.
      sum += leg.groundspeedKt - leg.tasKt;
      count++;
    }
    return count > 0 ? sum / count : null;
  }, [winds, calc.legs]);

  // ---- Ident entry --------------------------------------------------------
  const [identQ, setIdentQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [bulkUnresolved, setBulkUnresolved] = useState<string[]>([]);

  // Stale unresolved warnings disappear as soon as the user starts typing
  // again — otherwise the amber list lingers across unrelated edits.
  useEffect(() => {
    if (bulkUnresolved.length > 0) setBulkUnresolved([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identQ]);

  // Debounced typeahead for the inline search.
  useEffect(() => {
    const ac = new AbortController();
    if (!identQ.trim()) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await searchAny(identQ, { limit: 10, signal: ac.signal });
        if (!ac.signal.aborted) setHits(res);
      } catch { /* ignore */ }
    }, 150);
    return () => { ac.abort(); clearTimeout(t); };
  }, [identQ]);

  const onSubmitBulk = async () => {
    const tokens = identQ.trim().split(/[\s,]+/).filter(Boolean);
    if (tokens.length === 0) return;
    // Resolve each token concurrently and contain individual rejections
    // so one network blip can't abort the whole batch.
    const results = await Promise.all(
      tokens.map((t) =>
        resolveIdent(t).then(
          (r) => ({ ok: true as const, t, r }),
          ()  => ({ ok: false as const, t, r: null }),
        ),
      ),
    );
    const unresolved: string[] = [];
    // Insert in the order the user typed them (Promise.all preserves order).
    for (const item of results) {
      if (!item.ok || !item.r) { unresolved.push(item.t); continue; }
      const r = item.r;
      add({ ident: r.ident, lat: r.lat, lon: r.lon, kind: r.kind });
    }
    setIdentQ('');
    setHits([]);
    setBulkUnresolved(unresolved);
  };

  const pickHit = (h: SearchHit) => {
    add({
      ident: h.ident,
      lat: h.lat,
      lon: h.lon,
      kind: hitToPlanKind(h),
    });
    setIdentQ('');
    setHits([]);
  };

  // ---- Save flight --------------------------------------------------------
  const [savedHint, setSavedHint] = useState<string | null>(null);
  // Track the hint timer in a ref so we can clear it on unmount or before
  // scheduling a new one. Without this, fast successive saves stack
  // setTimeouts that can call setSavedHint after the panel closes.
  const savedTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
  }, []);
  const onSaveFlight = async () => {
    if (route.length < 2) return;
    // Defensive: if ETD is still null at save time, materialize the
    // wheel's displayed default (next 5-minute slot) so the saved flight
    // always has a usable ETD. The TimeWheel pushes this on mount but a
    // race (panel opened + saved within the same render tick) could
    // leave planStore.etd null. (2026-05-18 boss report)
    const etdForSave = etd ?? (() => {
      const fb = nextFiveMinuteSlotLocal();
      const iso = composeIsoFromParts(fb.date, fb.hour, fb.minute);
      setEtd(iso); // keep store in sync so the flight detail page reads the same value
      return iso;
    })();
    const f = await createFlight({
      title: flightTitle(route),
      route,
      etd: etdForSave,
      cruiseAlt,
      aircraftId,
      performanceProfileId,
      remarks: '',
    });
    setSavedHint(`Saved as “${f.title}”`);
    if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => setSavedHint(null), 3000);
    // Close the floating plan panel and navigate to the flight detail
    // page so the pilot lands on a confirmation view with the full
    // flight breakdown (route table, plates, weather, etc.).
    onClose();
    nav(`/flights/${f.id}`);
  };

  // ---- Pack for flight ----------------------------------------------------
  // Pre-cache tiles + API data along the route corridor so the EFB
  // works offline mid-flight. The service worker (public/sw.js)
  // transparently intercepts and stashes the fetched responses.
  const packPhase   = usePackStore((s) => s.phase);
  const packCurrent = usePackStore((s) => s.current);
  const packStatus  = usePackStore((s) => s.status);
  const packStart   = usePackStore((s) => s.startPack);
  const packCancel  = usePackStore((s) => s.cancel);
  const packReset   = usePackStore((s) => s.reset);
  // Current chart overlay on the map — fed to the packer as the
  // "prefer" hint so the user's visible chart is always packed.
  const chartOverlay = useUiStore((s) => s.chartOverlay);
  const onPack = () => {
    if (route.length < 1) {
      alert('Add at least one route point before packing.');
      return;
    }
    if (packPhase === 'downloading' || packPhase === 'planning') return;
    // Hand the user's live plan context to the packer so it can pick
    // the right chart layers (altitude + rule) + cache the linked
    // aircraft's performance profile. Boss spec 2026-05-25 16:15 PT.
    void packStart({
      route,
      label: route.length >= 2
        ? `${route[0].ident} \u2192 ${route[route.length - 1].ident}`
        : route[0].ident,
      cruiseAlt,
      cruiseRule,
      chartLayer: chartOverlay !== 'none' ? chartOverlay : null,
      aircraftProfileId: performanceProfileId,
    });
  };
  const packBusy = packPhase === 'planning' || packPhase === 'downloading';
  const packProgressPct =
    packCurrent && packCurrent.total > 0
      ? Math.min(100, Math.round(((packCurrent.ok + packCurrent.fail) / packCurrent.total) * 100))
      : 0;

  const isDesktop = useIsDesktop();

  // Wrapper varies by viewport — mobile is a floating sheet, desktop is
  // a permanent master-panel column to the right of the rail.
  const Wrapper = isDesktop ? DesktopWrapper : MobileWrapper;
  const headerActions = (
    <>
      {route.length >= 2 && (
        <button
          onClick={onSaveFlight}
          className="text-[11px] bg-cockpit-700 hover:bg-cockpit-600 border border-cockpit-600 rounded-md px-2 py-1"
        >
          Save flight
        </button>
      )}
    </>
  );

  return (
    <Wrapper title="Flight Plan" onClose={onClose} actions={headerActions}>
      <div className="p-3 space-y-4">
        {/* ---- TFR CONFLICT BANNER ------------------------------------ */}
        {tfrConflicts.length > 0 && !conflictsDismissed && (
          <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={14} className="text-yellow-400 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-yellow-300 mb-0.5">
                Route conflicts with {tfrConflicts.length} active TFR{tfrConflicts.length > 1 ? 's' : ''}
              </div>
              {tfrConflicts.slice(0, 3).map((c) => (
                <div key={c.notamId} className="text-[10px] text-yellow-200/80 leading-snug">
                  TFR {c.notamId} · {c.altitudeRange}
                  {c.description ? ` · ${c.description.slice(0, 60)}${c.description.length > 60 ? '…' : ''}` : ''}
                </div>
              ))}
              {tfrConflicts.length > 3 && (
                <div className="text-[10px] text-yellow-400/70">
                  +{tfrConflicts.length - 3} more TFR{tfrConflicts.length - 3 > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <button
              onClick={() => setConflictsDismissed(true)}
              className="text-yellow-500 hover:text-yellow-300 text-xs shrink-0"
              aria-label="Dismiss TFR conflict warning"
            >
              ✕
            </button>
          </div>
        )}

        {/* ---- 1. ROUTE ENTRY ----------------------------------------- */}
        <Section title="Route">
          <div className="flex gap-2 items-center">
            <input
              value={identQ}
              onChange={(e) => setIdentQ(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && onSubmitBulk()}
              placeholder="KSIY OED FJS BOACH KSFO"
              aria-label="Add waypoints by ident"
              className="flex-1 bg-cockpit-700 border border-cockpit-600 rounded-md px-2 py-1.5 text-sm font-mono"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              onClick={onSubmitBulk}
              className="bg-ember-600 hover:bg-ember-500 text-white text-sm rounded-md px-3 py-1.5"
            >
              Add
            </button>
          </div>

          {hits.length > 0 && (
            <ul className="mt-1 max-h-48 overflow-y-auto scroll-touch border border-cockpit-700 rounded-md divide-y divide-cockpit-700">
              {hits.map((h) => (
                <li key={`${h.kind}-${h.ident}`}>
                  <button
                    onClick={() => pickHit(h)}
                    className="w-full text-left px-2 py-1.5 hover:bg-cockpit-700"
                  >
                    <span className="font-mono text-sm text-cockpit-100">{h.ident}</span>
                    <span className="text-[11px] text-cockpit-300 ml-2">{h.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {bulkUnresolved.length > 0 && (
            <div className="mt-1 text-[11px] text-amber-400">
              Unresolved: {bulkUnresolved.join(', ')}
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => setPlanClick(!planClick)}
              className={[
                'text-[11px] rounded-md px-2 py-1 border',
                planClick
                  ? 'bg-ember-600/30 border-ember-500 text-ember-200'
                  : 'bg-cockpit-700 border-cockpit-600 text-cockpit-200',
              ].join(' ')}
            >
              {planClick ? 'Tap-to-add: ON' : 'Tap-to-add: OFF'}
            </button>
            {route.length > 0 && (
              <button onClick={() => clear()} className="text-[11px] text-red-400 hover:text-red-300">
                Clear route
              </button>
            )}
          </div>

          {route.length > 0 && (
            <RouteList route={route} move={move} remove={remove} />
          )}
        </Section>

        {/* ---- 2. AIRCRAFT ------------------------------------------- */}
        <Section title="Aircraft">
          <select
            value={aircraftId ?? ''}
            onChange={(e) => setAcId(e.target.value || null)}
            className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-2"
          >
            <option value="">— select aircraft —</option>
            {aircraftList.map((a) => (
              <option key={a.id} value={a.id}>
                {a.tail}{a.typeLabel ? ` · ${a.typeLabel}` : ''}
              </option>
            ))}
          </select>

          {/* Performance profile picker — same shape as the FlightDetail
              page. Hidden until an aircraft is picked AND that aircraft
              has at least one published profile. 2026-05-17. */}
          {aircraft && aircraft.performanceProfiles.length > 0 && (
            <div className="mt-2 space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-cockpit-400">
                Performance profile
              </label>
              <select
                value={performanceProfileId ?? ''}
                onChange={(e) => setPerfProfileId(e.target.value || null)}
                className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5"
              >
                <option value="">— Default —</option>
                {aircraft.performanceProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
              {perfProfile && (
                <div className="text-[11px] text-cockpit-300">
                  {perfProfile.cruiseKtas} KTAS · {perfProfile.fuelBurnGph} GPH
                  {perfProfile.power ? ` · ${perfProfile.power}` : ''}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ---- 3. CRUISE ALTITUDE ------------------------------------ */}
        <Section
          title="Cruise altitude"
          titleRight={
            // VFR / IFR segmented toggle
            <div className="flex rounded-md overflow-hidden border border-cockpit-600 text-[11px] font-medium">
              {(['vfr', 'ifr'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setCruiseRule(r)}
                  className={[
                    'px-2.5 py-0.5 transition-colors',
                    cruiseRule === r
                      ? 'bg-ember-600 text-white'
                      : 'bg-cockpit-700 text-cockpit-300 hover:text-cockpit-100',
                  ].join(' ')}
                >
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          }
        >
          {/* Altitude dropdown — only compliant altitudes for current rule + course. */}
          {(() => {
            // Is the current cruiseAlt in the options list?
            const inList = cruiseAlt != null && altOptions.includes(cruiseAlt);
            const isNonStandard = cruiseAlt != null && !inList;
            return (
              <select
                value={cruiseAlt ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Number(e.target.value);
                  setCruise(val);
                  if (altWarning) setAltWarning(null);
                }}
                className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md px-2 py-1.5 text-sm font-mono"
              >
                {/* Placeholder when nothing is selected */}
                {cruiseAlt == null && (
                  <option value="" disabled>Select cruise altitude</option>
                )}
                {/* Non-standard synthetic option (legacy / corner-case) */}
                {isNonStandard && (
                  <option value={cruiseAlt!}>
                    {formatAltitudeForDisplay(cruiseAlt!)} (non-standard)
                  </option>
                )}
                {altOptions.map((alt) => (
                  <option key={alt} value={alt}>
                    {formatAltitudeForDisplay(alt)}
                  </option>
                ))}
              </select>
            );
          })()}
          {/* Auto-bump warning */}
          {altWarning && !altOverride && (
            <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-400">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{altWarning}</span>
            </div>
          )}
          {/* Hemispheric-rule info hint */}
          <div className="mt-1 text-[10px] text-cockpit-400">
            {altOverride
              ? 'Hemispheric rule override — 500-ft increments available'
              : hemisphericRuleLabel(hemisphericInput)}
          </div>
          {/* Override toggle: allow non-compliant altitudes. 2026-05-17. */}
          <label className="mt-1 flex items-center gap-1.5 text-[11px] text-cockpit-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={altOverride}
              onChange={(e) => {
                setAltOverride(e.target.checked);
                if (e.target.checked) setAltWarning(null);
              }}
              className="h-3 w-3 accent-ember-500"
            />
            <span>Override hemispheric rule (allow any 500-ft altitude)</span>
          </label>
        </Section>

        {/* ---- 4. PROCEDURES (live) ---------------------------------- */}
        <Section title="Procedures" sub="DPs · STARs · IAPs from CIFP">
          <ProceduresSection />
        </Section>

        {/* ---- TERRAIN PROFILE --------------------------------------- */}
        {/* USGS 3DEP elevation sampled along the great-circle route.
            Hides when route has <2 waypoints. Boss 2026-05-19 02:01 PT. */}
        {route.length >= 2 && (
          <Section title="Terrain profile" sub="USGS 3DEP · along-route elevation">
            <RouteProfile
              waypoints={route.map((p) => ({ ident: p.ident, lat: p.lat, lon: p.lon }))}
              cruiseAltFt={cruiseAlt ?? null}
              heightPx={180}
              compact
            />
          </Section>
        )}

        {/* ---- TERRAIN-CLEARANCE AUTO-ROUTER (Wiley 2026-05-19) ----- */}
        {/* Lives below the terrain profile so the visual reference is
            right there when the pilot tweaks corridor/altitude.
            Reuses the cruise altitude from the Cruise altitude section.
            Origin = first airport in the route; destination = last
            airport (or last waypoint if there's no second airport).
            Produces a sparse detour route (typically 3–6 waypoints)
            and injects it between the endpoints. */}
        {route.length >= 2 && (() => {
          const firstAp = route.find((p) => (p as { kind?: string }).kind === 'airport');
          let lastAp: (typeof route)[number] | undefined;
          for (let i = route.length - 1; i >= 0; i--) {
            if ((route[i] as { kind?: string }).kind === 'airport') { lastAp = route[i]; break; }
          }
          // Endpoints fall back to first/last route waypoint when the
          // user has only one airport (or none) in the plan.
          const origin = firstAp ?? route[0];
          const dest   = (lastAp && lastAp !== firstAp) ? lastAp : route[route.length - 1];
          const canRoute = origin != null && dest != null && origin !== dest && cruiseAlt != null;
          return (
            <Section title="Terrain-clearance auto-router" sub="Wiley · A* over corridor">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <label className="text-[11px] text-cockpit-300 flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={avoidTfrs}
                    onChange={(e) => setAvoidTfrs(e.target.checked)}
                    disabled={useCanyonRouter}
                    className="h-3 w-3 accent-ember-500"
                  />
                  Avoid TFRs
                </label>
                <label className="text-[11px] text-cockpit-300 flex items-center gap-1.5">
                  Corridor
                  <select
                    value={corridorNm}
                    onChange={(e) => setCorridorNm(Number(e.target.value))}
                    className="bg-cockpit-700 border border-cockpit-600 rounded px-1.5 py-0.5 text-[11px] font-mono"
                  >
                    {[10, 15, 20, 30, 40, 60].map((n) => (
                      <option key={n} value={n}>{n} NM</option>
                    ))}
                  </select>
                </label>
                <label
                  className="text-[11px] text-cyan-300 flex items-center gap-1.5 cursor-pointer select-none"
                  title="Dense client-side A* using in-memory Terrarium DEM tiles. Threads canyons that the server router misses."
                >
                  <input
                    type="checkbox"
                    checked={useCanyonRouter}
                    onChange={(e) => setUseCanyonRouter(e.target.checked)}
                    className="h-3 w-3 accent-cyan-400"
                  />
                  Canyon router (beta)
                </label>
              </div>
              <button
                type="button"
                disabled={!canRoute || autoRouteBusy}
                onClick={async () => {
                  if (!canRoute || cruiseAlt == null || !origin || !dest) return;
                  setAutoRouteBusy(true);
                  setAutoRouteErr(null);
                  setAutoRouteInfo(null);
                  setCanyonProgress(null);
                  try {
                    let r:
                      | { ok: true; waypoints: Array<{ ident?: string; lat: number; lon: number; note?: string }>; diagnostics: PassFinderDiagnostics }
                      | { ok: false; error: string }
                      | null = null;
                    if (useCanyonRouter) {
                      const cr = await findCanyonRoute({
                        origin:      { ident: origin.ident, lat: origin.lat, lon: origin.lon },
                        destination: { ident: dest.ident,   lat: dest.lat,   lon: dest.lon   },
                        cruiseAltFt: cruiseAlt,
                        corridorHalfNm: corridorNm / 2,
                        onProgress: (phase, detail) => {
                          setCanyonProgress(
                            phase === 'preload'
                              ? 'Loading terrain tiles…'
                              : (detail ?? 'Computing route…'),
                          );
                        },
                      });
                      if (cr.ok === false) {
                        r = cr;
                      } else {
                        // Adapt CanyonRouterDiagnostics → PassFinderDiagnostics
                        // shape used by the existing renderer below. No
                        // passesUsed/tfrsAvoided yet on the canyon path.
                        const d: CanyonRouterDiagnostics = cr.diagnostics;
                        r = {
                          ok: true,
                          waypoints: cr.waypoints,
                          diagnostics: {
                            directDistanceNm: d.directDistanceNm,
                            routedDistanceNm: d.routedDistanceNm,
                            directMaxTerrainFt: d.directMaxTerrainFt,
                            routedMaxTerrainFt: d.routedMaxTerrainFt,
                            directMinAglFt: d.directMinAglFt,
                            routedMinAglFt: d.routedMinAglFt,
                            passesUsed: [],
                            tfrsAvoided: [],
                            reason: d.reason,
                            softFallback: d.softFallback,
                          },
                        };
                      }
                    } else {
                      r = await findTerrainClearanceRoute({
                        origin:      { ident: origin.ident, lat: origin.lat, lon: origin.lon },
                        destination: { ident: dest.ident,   lat: dest.lat,   lon: dest.lon   },
                        cruiseAltFt: cruiseAlt,
                        corridorHalfNm: corridorNm / 2,
                        avoidTfrs,
                      });
                    }
                    if (!r || r.ok === false) {
                      setAutoRouteErr(r?.ok === false ? r.error : 'Auto-router returned no response');
                      return;
                    }
                    setAutoRouteInfo(r.diagnostics);

                    // Inject detour/pass waypoints between origin & destination.
                    const interior = r.waypoints.filter((w) => w.note !== 'origin' && w.note !== 'destination');
                    if (interior.length === 0) {
                      // Nothing to insert. Distinguish the three real cases:
                      //   - no-conflict:   direct route already safe
                      //   - no-solution:   A* couldn't find ANY path that meets
                      //                    clearance (even with soft-mode fallback)
                      //   - success/soft:  A* found a path but Douglas-Peucker
                      //                    collapsed it back to the great-circle
                      //                    (rare; means the path is mostly straight)
                      if (r.diagnostics.reason === 'no-conflict') {
                        setAutoRouteErr('Direct route already clears terrain — no change needed.');
                      } else if (r.diagnostics.reason === 'no-solution') {
                        setAutoRouteErr(
                          'No safe route found within the corridor at this altitude. ' +
                          'Try a wider corridor or higher cruise altitude.',
                        );
                      } else {
                        setAutoRouteErr(
                          'Auto-router returned no detour waypoints. ' +
                          'The corridor may be too narrow to deflect around terrain — try widening it.',
                        );
                      }
                      return;
                    }
                    const pts: PlanPoint[] = interior.map((w) => ({
                      ident: w.ident ?? `WP${String(Math.round(w.lat * 100))}_${String(Math.round(w.lon * 100))}`,
                      lat: w.lat,
                      lon: w.lon,
                      kind: (w.note === 'pass' ? 'user' : 'user') as PlanPointKind,
                    }));

                    // Prefer replaceEnrouteSegment when we have two
                    // bona-fide airports as origin & destination (it
                    // preserves loaded SIDs/STARs/IAPs). Otherwise
                    // manually rebuild [origin, ...pts, destination].
                    if (firstAp && lastAp && firstAp !== lastAp) {
                      replaceEnrouteSegment(pts);
                    } else {
                      // Manual rebuild: clear non-procedure middle, insert pts.
                      clear();
                      add({ ident: origin.ident, lat: origin.lat, lon: origin.lon, kind: origin.kind ?? 'user' });
                      for (const p of pts) add(p);
                      add({ ident: dest.ident, lat: dest.lat, lon: dest.lon, kind: dest.kind ?? 'user' });
                    }
                  } catch (err) {
                    setAutoRouteErr((err as Error)?.message ?? String(err));
                  } finally {
                    setAutoRouteBusy(false);
                    setCanyonProgress(null);
                  }
                }}
                className="w-full bg-ember-600/20 hover:bg-ember-600/35 disabled:opacity-40 disabled:hover:bg-ember-600/20 border border-ember-600/40 text-ember-200 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors"
              >
                {autoRouteBusy
                  ? (canyonProgress ?? (useCanyonRouter ? 'Routing through canyons…' : 'Finding terrain-clear route…'))
                  : (useCanyonRouter ? 'Find canyon route' : 'Find terrain-clearance route')}
              </button>
              {!canRoute && (
                <div className="mt-1 text-[10px] text-cockpit-400">
                  Need an origin + destination and a cruise altitude.
                </div>
              )}
              {autoRouteErr && (
                <div className="mt-1 text-[11px] text-amber-300 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>{autoRouteErr}</span>
                </div>
              )}
              {autoRouteInfo && !autoRouteErr && (
                <div className="mt-2 text-[11px] text-cockpit-300 space-y-0.5">
                  {autoRouteInfo.reason === 'no-conflict' && (
                    <div className="text-emerald-300">
                      Direct route already clears terrain at {cruiseAlt?.toLocaleString()}'.
                    </div>
                  )}
                  {autoRouteInfo.reason === 'no-solution' && (
                    <div className="text-amber-300">
                      No safe route found within the corridor — widen corridor or raise cruise.
                    </div>
                  )}
                  {autoRouteInfo.reason === 'success-soft' && (
                    <div className="text-red-400 font-medium">
                      ⚠ No route fully clears terrain at this altitude. Showing best-effort
                      detour that still overflies high terrain — raise cruise altitude.
                    </div>
                  )}
                  {(autoRouteInfo.reason === 'success' || autoRouteInfo.reason === 'success-soft') && (
                    <>
                      <div>
                        Direct {autoRouteInfo.directDistanceNm.toFixed(0)} NM ·
                        {' '}max terrain {autoRouteInfo.directMaxTerrainFt.toLocaleString()}'
                        {' '}({autoRouteInfo.directMinAglFt >= 0 ? `+${autoRouteInfo.directMinAglFt.toLocaleString()}` : autoRouteInfo.directMinAglFt.toLocaleString()}' AGL)
                      </div>
                      <div className="text-emerald-300">
                        Routed {autoRouteInfo.routedDistanceNm.toFixed(0)} NM ·
                        {' '}max terrain {autoRouteInfo.routedMaxTerrainFt.toLocaleString()}'
                        {' '}(+{autoRouteInfo.routedMinAglFt.toLocaleString()}' AGL)
                      </div>
                      {autoRouteInfo.passesUsed.length > 0 && (
                        <div className="text-cyan-200/90">
                          Via passes: {autoRouteInfo.passesUsed.join(', ')}
                        </div>
                      )}
                      <div className="text-cockpit-400 text-[10px] mt-1">
                        Inserted detour waypoints into your flight plan above.
                      </div>
                    </>
                  )}
                </div>
              )}
            </Section>
          );
        })()}

        {/* ---- 5. ETD TIME WHEEL ------------------------------------- */}
        <Section title="ETD" sub="date · hour · minute · 24h local">
          <TimeWheel value={etd} onChange={setEtd} />
          {etd && (
            <button
              onClick={() => setEtd(null)}
              className="mt-2 text-[11px] text-cockpit-300 hover:text-cockpit-100"
            >
              Clear ETD
            </button>
          )}
        </Section>

        {/* ---- 6. ROUTE ADVISOR (opt-in) ----------------------------- */}
        <Section title="Route advisor" sub="FAA preferred IFR + recent clearances — loads on demand">
          <RouteAdvisor />
        </Section>

        {/* ---- 7. LIVE CALC ------------------------------------------ */}
        <Section title="Calc" sub="updates as you edit">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <Stat label="DIST" value={calc.totalNm > 0 ? formatNm(calc.totalNm) : '—'} />
            <Stat label="ETE" value={formatHm(calc.totalEteMinutes ?? null)} />
            <Stat label="ETA" value={eta ? formatLocalTime(eta) : '—'} />
            <Stat
              label="FUEL"
              value={calc.totalFuelGal != null ? `${calc.totalFuelGal.toFixed(1)} gal` : '—'}
            />
            <Stat
              label="AVG WIND"
              value={
                windsLoading ? '…' :
                avgHeadwindKt == null ? '—' :
                avgHeadwindKt > 0 ? `+${Math.round(avgHeadwindKt)}kt tail` :
                avgHeadwindKt < 0 ? `${Math.round(avgHeadwindKt)}kt head` :
                '0 kt'
              }
              tooltip="Average wind component along route (vs cruise TAS). Positive = tailwind, negative = headwind. Source: GFS+HRRR via Open-Meteo."
            />
            <Stat
              label="LEGS"
              value={calc.legs.length > 0 ? String(calc.legs.length) : '—'}
            />
          </div>

          {calc.legs.length > 0 && (
            <details className="mt-1">
              <summary className="text-[11px] text-cockpit-300 cursor-pointer">Per-leg breakdown</summary>
              <table className="w-full text-[11px] mt-1">
                <thead className="text-[10px] tracking-widest text-cockpit-400">
                  <tr>
                    <th className="text-left">FROM</th>
                    <th className="text-left">TO</th>
                    <th className="text-left">DIST</th>
                    <th className="text-left">TC</th>
                    <th className="text-left">ETE</th>
                  </tr>
                </thead>
                <tbody>
                  {calc.legs.map((l, i) => (
                    <tr key={i} className="border-t border-cockpit-700">
                      <td className="py-1 font-mono">{l.from.ident}</td>
                      <td className="py-1 font-mono">{l.to.ident}</td>
                      <td className="py-1">{formatNm(l.distanceNm)}</td>
                      <td className="py-1">{formatBrg(l.trueCourseDeg)}</td>
                      <td className="py-1">{l.eteMinutes != null ? `${Math.round(l.eteMinutes)} min` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </Section>

        {/* ---- 7b. DISPATCH & COMMS ----------------------------------- *
         * Moved to FlightDetailPage as part of Phase 2 ARTCC handoff
         * work. Pilots see the full comm sequence (NIFC repeaters +
         * predicted ARTCC handoffs) on the saved-flight surface. The
         * unsaved-plan picker no longer needs a teaser because Save
         * flight already navigates straight to the detail page. */}

        {/* ---- 8. PACK FOR FLIGHT ------------------------------------- */}
        {!packBusy && (
          <button
            onClick={onPack}
            className="w-full bg-cockpit-700 hover:bg-cockpit-600 border border-cockpit-600 rounded-lg py-2.5 flex items-center justify-center gap-2 text-sm"
          >
            <Icon name="pack" /> Pack for flight
          </button>
        )}
        {packBusy && (
          <div className="space-y-2">
            <PackProgressSheet />
            <button
              onClick={packCancel}
              className="w-full text-[11px] text-cockpit-400 hover:text-cockpit-200"
            >
              Cancel pack
            </button>
          </div>
        )}
        {!packBusy && packCurrent && packCurrent.categories && (packPhase === 'ready' || packPhase === 'failed' || packPhase === 'canceled') && (
          <PackProgressSheet />
        )}
        {(packPhase === 'ready' || packPhase === 'failed' || packPhase === 'canceled') && packCurrent && (
          <div className="text-[11px] text-center text-cockpit-300 space-y-0.5">
            <div>
              {packPhase === 'ready' && '✅ '}
              {packPhase === 'failed' && '⚠️ '}
              {packPhase === 'canceled' && '⏹ '}
              {packStatus}
            </div>
            <button onClick={packReset} className="text-cockpit-400 hover:text-cockpit-200 underline">
              dismiss
            </button>
          </div>
        )}

        {savedHint && (
          <div className="text-[11px] text-emerald-400 text-center">{savedHint}</div>
        )}

        <p className="text-[10px] text-cockpit-400 leading-snug">
          Calculated values use selected aircraft cruise/burn rates and assume no wind. Verify all numbers
          with your performance manuals and current conditions before flight.
        </p>
      </div>
    </Wrapper>
  );
}

// ---------------------------------------------------------------------------
// Wrappers — mobile floating sheet vs desktop master column.
// ---------------------------------------------------------------------------

interface WrapperProps {
  title: string;
  onClose: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

function MobileWrapper({ title, onClose, actions, children }: WrapperProps) {
  return (
    <div
      className="absolute z-30 left-2 right-2 bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail max-h-[80vh] overflow-y-auto scroll-touch"
      style={{ top: 'calc(var(--safe-top) + 7.5rem)' }}
    >
      <div className="sticky top-0 z-10 bg-cockpit-800/95 border-b border-cockpit-700 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="route" />
          <h2 className="font-semibold text-sm">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          <button onClick={onClose} className="text-cockpit-300 text-xs">Close</button>
        </div>
      </div>
      {children}
    </div>
  );
}

function DesktopWrapper({ title, onClose, actions, children }: WrapperProps) {
  return (
    // Pinned along the right edge of the map area on desktop, below the
    // top command bar. Right-side placement matches the auto-fit-to-route
    // padding (right: 460) in MapView.tsx — keep them aligned.
    <aside
      className="hidden lg:flex absolute z-20 bottom-0 right-0 w-[26rem] xl:w-[30rem] bg-cockpit-800/95 border-l border-cockpit-700 shadow-rail flex-col"
      style={{ top: 'calc(var(--safe-top) + 3.5rem)' }}
    >
      <header className="flex items-center justify-between px-3 h-12 border-b border-cockpit-700 shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="route" />
          <h2 className="font-semibold text-sm text-cockpit-100">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          <button
            onClick={onClose}
            className="text-cockpit-300 hover:text-cockpit-100 h-8 w-8 flex items-center justify-center rounded"
            aria-label="Close"
            title="Close panel"
          >✕</button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto scroll-touch">{children}</div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  sub,
  titleRight,
  children,
}: { title: string; sub?: string; titleRight?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] tracking-widest text-cockpit-300">{title.toUpperCase()}</span>
          {sub && <span className="text-[9px] text-cockpit-400 italic">{sub}</span>}
        </div>
        {titleRight}
      </div>
      <div>{children}</div>
    </div>
  );
}

/**
 * Map a /api/search hit's kind+badge to the planStore's PlanPointKind.
 * Mirrors the logic in src/lib/ident.ts so navaid badges (NDB, VORTAC,
 * VOR-DME, etc.) are classified properly instead of being collapsed to
 * 'vor'.
 */
function hitToPlanKind(h: SearchHit): PlanPointKind {
  if (h.kind === 'airport') return 'airport';
  if (h.kind === 'fix') return 'fix';
  return (h.badge || '').toUpperCase().includes('NDB') ? 'ndb' : 'vor';
}

function Stat({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <div
      title={tooltip}
      className="bg-cockpit-700/40 border border-cockpit-700 rounded-md px-2 py-1.5 text-center"
    >
      <div className="text-[9px] tracking-widest text-cockpit-400">{label}</div>
      <div className="font-mono text-cockpit-100 text-sm leading-tight">{value}</div>
    </div>
  );
}

// ─── Route list with drag-and-drop reordering ─────────────────────────────
//
// Desktop: native HTML5 drag-and-drop on each <li>. The grip handle
// shows where to grab. Drop on a row reorders ABOVE that row; drop
// past the last row pushes to the end.
//
// Touch: native HTML5 drag is unreliable on iOS Safari. The ↑ / ↓
// arrows stay as the touch fallback, plus a long-press-to-drag
// pointer-event handler engaged via the grip icon. The pointer path
// uses a small drag-state machine: long-press 300ms on the grip
// engages drag mode, pointermove tracks which row the finger is over,
// pointerup commits the reorder. The dragged row shows reduced
// opacity; the drop target shows an ember insertion line.

interface RouteListProps {
  route: PlanPoint[];
  move: (from: number, to: number) => void;
  remove: (idx: number) => void;
}

// Procedure-group flagging + labeled header rows live in lib/planRowGroups
// so they're independently unit-testable. See planRowGroups.test.ts.
import { computeProcGroups, isHiddenInPlanList } from '../lib/planRowGroups';

function RouteList({ route, move, remove }: RouteListProps) {
  const procGroups = useMemo(
    () => computeProcGroups(route as Array<{ procedureTag?: any }>),
    [route],
  );
  // Active drag indices. dragFrom = index being moved; dragOver = the
  // index we'd drop ABOVE if released right now. dragOver = route.length
  // means "drop past the last row" (append).
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Route index of the waypoint currently having a hold added via the
  // Garmin-style hold dialog. null = closed.
  const [holdIdx, setHoldIdx] = useState<number | null>(null);

  // Long-press / pointer-drag state machine. Tracked outside React so
  // pointermove fires don't re-render on every pixel.
  const pointerRef = useRef<{
    pointerId: number;
    startIdx: number;
    longPressTimer: number | null;
    engaged: boolean;
  } | null>(null);

  function commitDrop(from: number, overIdx: number) {
    const target = computeReorderTarget(route.length, from, overIdx);
    if (target == null) return;
    move(from, target);
  }

  function findRowIndexAtClientY(clientY: number): number {
    const ul = listRef.current;
    if (!ul) return -1;
    const lis = Array.from(ul.querySelectorAll<HTMLLIElement>('li[data-route-idx]'));
    for (const li of lis) {
      const r = li.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) {
        const idx = Number(li.dataset.routeIdx);
        // Top half → drop ABOVE this idx; bottom half → drop BELOW (idx+1)
        return clientY < r.top + r.height / 2 ? idx : idx + 1;
      }
    }
    // Past the last row → append.
    return route.length;
  }

  function onGripPointerDown(idx: number, e: React.PointerEvent) {
    // Only handle touch / pen here — mouse uses the native HTML5 drag path.
    if (e.pointerType === 'mouse') return;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    pointerRef.current = {
      pointerId: e.pointerId,
      startIdx: idx,
      longPressTimer: window.setTimeout(() => {
        const s = pointerRef.current;
        if (!s) return;
        s.engaged = true;
        setDragFrom(idx);
        if ('vibrate' in navigator) {
          try { (navigator as Navigator & { vibrate: (p: number) => void }).vibrate(15); } catch { /* ignore */ }
        }
      }, 300),
      engaged: false,
    };
  }

  function onGripPointerMove(e: React.PointerEvent) {
    const s = pointerRef.current;
    if (!s || !s.engaged) return;
    e.preventDefault();
    const overIdx = findRowIndexAtClientY(e.clientY);
    setDragOver(overIdx);
  }

  function onGripPointerUp(_e: React.PointerEvent) {
    const s = pointerRef.current;
    if (!s) return;
    if (s.longPressTimer != null) window.clearTimeout(s.longPressTimer);
    if (s.engaged && dragOver != null) {
      commitDrop(s.startIdx, dragOver);
    }
    pointerRef.current = null;
    setDragFrom(null);
    setDragOver(null);
  }

  return (
    <ul
      ref={listRef}
      className="mt-2 divide-y divide-cockpit-700 border border-cockpit-700 rounded-md select-none"
    >
      {route.flatMap((p, i) => {
        // Hide synthesized altitude-terminator helpers (Bug #1/#2/#5).
        if (isHiddenInPlanList(p as { procedureTag?: any })) return [];
        const isDragged = dragFrom === i;
        const showInsertAbove = dragOver === i && dragFrom != null && dragFrom !== i && dragFrom + 1 !== i;
        const showInsertBelowLast = i === route.length - 1 && dragOver === route.length && dragFrom != null && dragFrom !== i;
        const grp = procGroups[i];
        // Bracket color per procedure type (slightly distinct so SID/STAR/IAP
        // are visually telegraphed even in a busy route list).
        const grpBorder =
          grp.routeType === 'SID'  ? 'border-l-4 border-l-emerald-500'
          : grp.routeType === 'STAR' ? 'border-l-4 border-l-sky-500'
          : grp.routeType === 'IAP'  ? 'border-l-4 border-l-fuchsia-400'
          : '';
        // Procedure-group header label (e.g. "FOBRO1 Departure",
        // "I29R Approach", "I29R Missed") rendered as a non-interactive
        // pseudo-row before the first row of each contiguous procedure
        // block. Bug #4 (2026-05-16).
        const headerNode = grp.isFirstInGroup && grp.headerLabel
          ? (
            <li
              key={`hdr-${i}`}
              data-procedure-header={grp.procIdent ?? ''}
              className={[
                'px-2 py-1 text-[10px] font-semibold uppercase tracking-wider',
                'bg-cockpit-800/70 text-cockpit-200',
                grpBorder,
                'pointer-events-none select-none',
              ].filter(Boolean).join(' ')}
            >
              {grp.headerLabel}
            </li>
          )
          : null;
        const rowNode = (
          <li
            key={i}
            data-route-idx={i}
            draggable
            onDragStart={(e) => {
              setDragFrom(i);
              // For Firefox, dataTransfer must be set or drag is cancelled.
              e.dataTransfer.effectAllowed = 'move';
              try { e.dataTransfer.setData('text/plain', String(i)); } catch { /* iOS */ }
            }}
            onDragOver={(e) => {
              if (dragFrom == null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              const r = (e.currentTarget as HTMLLIElement).getBoundingClientRect();
              const overIdx = e.clientY < r.top + r.height / 2 ? i : i + 1;
              setDragOver(overIdx);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom != null && dragOver != null) commitDrop(dragFrom, dragOver);
              setDragFrom(null);
              setDragOver(null);
            }}
            onDragEnd={() => {
              setDragFrom(null);
              setDragOver(null);
            }}
            className={[
              'relative flex items-center gap-2 px-2 py-1.5 transition-opacity',
              isDragged ? 'opacity-40' : '',
              showInsertAbove ? 'border-t-2 border-t-ember-500' : '',
              showInsertBelowLast ? 'border-b-2 border-b-ember-500' : '',
              // Procedure-group bracket: left border + indent for every
              // row that's part of a SID / STAR / IAP. Top/bottom borders
              // are NOT added on the first/last group rows so the
              // bracket reads as a single vertical span.
              grpBorder,
              grp.inGroup ? 'pl-3' : '',
            ].filter(Boolean).join(' ')}
          >
            <button
              type="button"
              className="h-6 w-5 flex items-center justify-center text-cockpit-400 hover:text-cockpit-200 cursor-grab active:cursor-grabbing touch-none"
              aria-label="Drag to reorder"
              onPointerDown={(e) => onGripPointerDown(i, e)}
              onPointerMove={onGripPointerMove}
              onPointerUp={onGripPointerUp}
              onPointerCancel={onGripPointerUp}
            >
              <GripVertical size={14} />
            </button>
            <span className="text-[10px] text-cockpit-200 w-5 font-mono">{i + 1}.</span>
            <RouteRowLabel point={p} />
            <button
              onClick={() => i > 0 && move(i, i - 1)}
              disabled={i === 0}
              className="h-6 w-6 text-cockpit-300 hover:text-cockpit-100 disabled:opacity-20"
              aria-label="Move up"
            >↑</button>
            <button
              onClick={() => i < route.length - 1 && move(i, i + 1)}
              disabled={i === route.length - 1}
              className="h-6 w-6 text-cockpit-300 hover:text-cockpit-100 disabled:opacity-20"
              aria-label="Move down"
            >↓</button>
            <button
              type="button"
              onClick={() => setHoldIdx(i)}
              className="h-6 w-6 text-cockpit-300 hover:text-fuchsia-300"
              aria-label="Add hold at this waypoint"
              title="Add hold at this waypoint"
            >
              <Repeat size={14} />
            </button>
            <button
              onClick={() => remove(i)}
              className="h-6 w-6 text-red-400 hover:text-red-300"
              aria-label="Remove waypoint"
            >×</button>
          </li>
        );
        return headerNode ? [headerNode, rowNode] : [rowNode];
      })}
      {holdIdx != null && (
        <HoldAtWaypointModal
          routeIndex={holdIdx}
          onClose={() => setHoldIdx(null)}
        />
      )}
    </ul>
  );
}

interface MaybeProc {
  procedureTag?: {
    placeholder?: boolean;
    synthetic?: boolean;
    hiddenInPlanList?: boolean;
    legType?: string;
    routeType?: 'SID' | 'STAR' | 'IAP';
    isMissedApproach?: boolean;
    isFaf?: boolean;
    isMap?: boolean;
    isIaf?: boolean;
    isStepdownFix?: boolean;
    isHoldingPattern?: boolean;
  };
}

/** Pick the most-specific role badge for a procedure waypoint. Order
 *  here is chart-priority: FAF supersedes IF, MAP supersedes everything
 *  at the runway threshold, stepdown only when nothing else applies. */
function roleBadge(tag: NonNullable<MaybeProc['procedureTag']>): { text: string; color: string } | null {
  if (tag.isFaf)            return { text: 'FAF',  color: 'text-amber-300 bg-amber-900/30 border border-amber-700' };
  if (tag.isMap)            return { text: 'MAP',  color: 'text-rose-200  bg-rose-900/40  border border-rose-600' };
  if (tag.isIaf)            return { text: 'IAF',  color: 'text-sky-200   bg-sky-900/30   border border-sky-600' };
  if (tag.isStepdownFix)    return { text: 'STEP', color: 'text-cyan-200  bg-cyan-900/30  border border-cyan-700' };
  if (tag.isHoldingPattern) return { text: 'HOLD', color: 'text-fuchsia-200 bg-fuchsia-900/30 border border-fuchsia-700' };
  return null;
}

function RouteRowLabel({ point }: { point: PlanPoint }) {
  const tag = (point as PlanPoint & MaybeProc).procedureTag;
  const placeholder = tag?.placeholder ?? false;
  const missed = tag?.isMissedApproach ?? false;
  const synthetic = tag?.synthetic ?? false;
  const legType = tag?.legType;
  const badge = tag ? roleBadge(tag) : null;
  const baseKind = tag?.routeType ? tag.routeType.toLowerCase() : (point.kind ?? '');
  const kindLabel =
      missed    ? `${baseKind} · missed`
    : synthetic ? `${baseKind} · ≈`
    : baseKind;
  return (
    <>
      <span className={[
        'flex-1 font-mono text-sm flex items-center gap-1.5',
        placeholder ? 'text-cockpit-300 italic' : 'text-cockpit-100',
        missed ? 'text-fuchsia-200' : '',
        synthetic && !missed ? 'text-fuchsia-200/80' : '',
      ].filter(Boolean).join(' ')}>
        <span>{point.ident}</span>
        {badge && (
          <span className={`px-1 py-px rounded text-[9px] font-sans font-semibold tracking-wide ${badge.color}`}>
            {badge.text}
          </span>
        )}
        {legType && !badge && (
          <span className="text-[9px] text-cockpit-200 font-sans">[{legType}]</span>
        )}
      </span>
      <span className={[
        'text-[10px] capitalize',
        missed ? 'text-fuchsia-300' : synthetic ? 'text-fuchsia-200/70' : 'text-cockpit-200',
      ].join(' ')}>{kindLabel}</span>
    </>
  );
}
